import React, { useState, useMemo, useCallback, useEffect } from 'react';
import '../styles/components/ImageGallery.css';
import { Image } from '../types';

interface ImageGalleryProps {
  images: Image[];
}

const ImageGallery: React.FC<ImageGalleryProps> = ({ images }) => {
  const [selectedImage, setSelectedImage] = useState<Image | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const [isFiltering, setIsFiltering] = useState(false);
  const [displayedImages, setDisplayedImages] = useState<Image[]>([]);
  const [removingImages, setRemovingImages] = useState<Set<number>>(new Set());
  const [enteringImages, setEnteringImages] = useState<Set<number>>(new Set());

  // Get unique categories from images
  const categories = useMemo(() => {
    const cats = [...new Set(images.map(img => img.category))];
    return ['All', ...cats];
  }, [images]);

  // Filter images based on selected category
  const filteredImages = useMemo(() => {
    if (selectedCategory === 'All') {
      return images;
    }
    return images.filter(img => img.category === selectedCategory);
  }, [images, selectedCategory]);

  // Group images by category
  const groupedImages = useMemo(() => {
    return filteredImages.reduce((groups, image) => {
      const category = image.category;
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(image);
      return groups;
    }, {} as Record<string, Image[]>);
  }, [filteredImages]);

  // Initialize displayed images
  useEffect(() => {
    setDisplayedImages(filteredImages);
  }, [filteredImages]);

  const handleCategoryFilter = useCallback((category: string) => {
    if (category === selectedCategory || isFiltering) return;

    setIsFiltering(true);
    setSelectedCategory(category);

    if (category === 'All') {
      // When going back to "All", mark all current images as removing first
      setRemovingImages(new Set(displayedImages.map(img => img.id)));
      
      setTimeout(() => {
        setDisplayedImages(images);
        setRemovingImages(new Set());
        setEnteringImages(new Set(images.map(img => img.id)));
        
        setTimeout(() => {
          setEnteringImages(new Set());
          setIsFiltering(false);
        }, 50);
      }, 300);
    } else {
      // Mark current images as removing
      setRemovingImages(new Set(displayedImages.map(img => img.id)));
      
      // Get new filtered images
      const newFilteredImages = images.filter(img => img.category === category);
      
      setTimeout(() => {
        setDisplayedImages(newFilteredImages);
        setRemovingImages(new Set());
        setEnteringImages(new Set(newFilteredImages.map(img => img.id)));
        
        setTimeout(() => {
          setEnteringImages(new Set());
          setIsFiltering(false);
        }, 50);
      }, 300);
    }
  }, [selectedCategory, isFiltering, displayedImages, images]);

  const toggleCategory = useCallback((category: string) => {
    setCollapsedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  }, []);

  const openModal = useCallback((image: Image) => {
    setSelectedImage(image);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setSelectedImage(null);
    setIsModalOpen(false);
  }, []);

  const nextImage = useCallback(() => {
    if (!selectedImage) return;
    const currentIndex = images.findIndex(img => img.id === selectedImage.id);
    const nextIndex = (currentIndex + 1) % images.length;
    setSelectedImage(images[nextIndex]);
  }, [selectedImage, images]);

  const prevImage = useCallback(() => {
    if (!selectedImage) return;
    const currentIndex = images.findIndex(img => img.id === selectedImage.id);
    const prevIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    setSelectedImage(images[prevIndex]);
  }, [selectedImage, images]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isModalOpen) return;
      
      switch (e.key) {
        case 'Escape':
          closeModal();
          break;
        case 'ArrowRight':
          nextImage();
          break;
        case 'ArrowLeft':
          prevImage();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen, selectedImage, images, nextImage, prevImage, closeModal]);

  if (images.length === 0) {
    return (
      <div className="gallery-container">
        <div className="no-images">No images available</div>
      </div>
    );
  }

  return (
    <div className="gallery-layout">
      {/* Category Sidebar */}
      <div className="category-sidebar">
        <h3>Categories</h3>
        <div className="category-buttons">
          {categories.map((category) => (
            <button
              key={category}
              className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
              onClick={() => handleCategoryFilter(category)}
              disabled={isFiltering}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Gallery Main Content */}
      <div className={`gallery-main ${isFiltering ? 'filtering' : ''}`}>
        {Object.entries(groupedImages).map(([category, categoryImages]) => (
          <div key={category} className={`category-section ${isFiltering ? 'filtering' : ''}`}>
            <h2 
              className={`category-header ${collapsedCategories.has(category) ? 'collapsed' : ''}`}
              onClick={() => toggleCategory(category)}
            >
              <span className="category-toggle">▼</span>
              {category} ({categoryImages.length})
            </h2>
            <div className={`category-content ${collapsedCategories.has(category) ? 'collapsed' : 'expanded'}`}>
              <div className="image-grid">
                {categoryImages.map((image) => {
                  const isRemoving = removingImages.has(image.id);
                  const isEntering = enteringImages.has(image.id);
                  
                  return (
                    <div
                      key={image.id}
                      data-image-id={image.id.toString()}
                      className={`image-item ${isRemoving ? 'removing' : ''} ${isEntering ? 'entering' : ''}`}
                      onClick={() => openModal(image)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openModal(image);
                        }
                      }}
                      aria-label={`View ${image.title || image.alt}`}
                    >
                      <div className="image-wrapper">
                        <img
                          src={image.url}
                          alt={image.alt}
                          loading="lazy"
                          srcSet={
                            image.url && /\.(jpg|jpeg)$/i.test(image.url)
                              ? `${image.url.replace(/\.(jpg|jpeg)$/i, '_thumb.$1')} 800w, ${image.url} 1920w`
                              : undefined
                          }
                          sizes="(max-width: 768px) 200px, (max-width: 1024px) 250px, 300px"
                        />
                        <div className="image-overlay">
                          <div className="image-title">{image.title || image.alt}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {isModalOpen && selectedImage && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeModal} aria-label="Close modal">×</button>
            <div className="modal-image-container">
              <img
                src={selectedImage.url}
                alt={selectedImage.alt}
                className="modal-image"
              />
              <div className="modal-info">
                <h3>{selectedImage.title || selectedImage.alt}</h3>
              </div>
            </div>
            <div className="modal-navigation">
              <button className="modal-nav-btn" onClick={prevImage} aria-label="Previous image">
                ‹
              </button>
              <button className="modal-nav-btn" onClick={nextImage} aria-label="Next image">
                ›
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageGallery;